import {
  Box,
  Button,
  Center,
  Divider,
  HStack,
  Icon,
  IconButton,
  Link,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Progress,
  Spacer,
  Spinner,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { debounce } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { TbArrowsUpDown, TbCheck, TbX } from "react-icons/tb";
import { RouterLink } from "~/components/Link";
import { BackgroundTask } from "./BackgroundTasks";
import { useBackgroundTaskManager, useBackgroundTasks } from "./hooks";

export function BackgroundTasksPopover() {
  const backgroundTaskManager = useBackgroundTaskManager();
  const tasks = useBackgroundTasks();
  const disclosure = useDisclosure();
  const isMouseOver = useRef(false);
  const [didMount, setDidMount] = useState(false);
  const isRunning = tasks.some((task) => task.status === "running");
  const mostRecentTask = tasks[tasks.length - 1];

  const debouncedOpenOnAdd = useMemo(() => {
    return debounce(() => {
      disclosure.onOpen();

      setTimeout(() => {
        if (isMouseOver.current) return;
        disclosure.onClose();
      }, 10_000);
    }, 250);
  }, []);

  useEffect(() => {
    if (!didMount) {
      setDidMount(true);
      return;
    }

    debouncedOpenOnAdd();
  }, [tasks.length]);

  if (tasks.length === 0 || !mostRecentTask) return null;

  const mostRecentTaskStatus = mostRecentTask.status;

  const isError = mostRecentTaskStatus === "rejected";

  function handleRemoveTask(id: string) {
    backgroundTaskManager.removeTask(id);
  }

  return (
    <Popover
      isLazy
      placement="bottom-start"
      isOpen={disclosure.isOpen}
      onClose={disclosure.onClose}
      onOpen={disclosure.onOpen}
    >
      <PopoverTrigger>
        <Button variant="unstyled" position={"relative"}>
          <Center>
            <Box position="relative" mt={2}>
              <Icon as={TbArrowsUpDown} fontSize="lg" />
              <Box
                position="absolute"
                top={-1}
                right={-3}
                fontSize="10px"
                color="white"
              >
                {!isRunning && (
                  <Icon
                    as={isError ? TbX : TbCheck}
                    bg={isError ? "red.500" : "green"}
                    borderRadius="full"
                    fontSize="15px"
                    p={0.5}
                  />
                )}
              </Box>
            </Box>
          </Center>
          {isRunning && (
            <Progress
              h="2px"
              size="xs"
              isIndeterminate
              isAnimated
              position={"absolute"}
              bottom={0}
              right={0}
              left={0}
            />
          )}
        </Button>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          color="black"
          fontFamily={"body"}
          minW={"400px"}
          onMouseEnter={() => {
            isMouseOver.current = true;
          }}
          onMouseLeave={() => {
            isMouseOver.current = false;
          }}
        >
          <PopoverArrow />
          <PopoverCloseButton />
          <PopoverHeader>
            <Stack spacing={0}>
              <Text>Background Tasks</Text>
              {isRunning && (
                <Text variant={"subdued"} fontSize="sm">
                  Pending tasks will be cancelled if you close this browser tab.
                </Text>
              )}
            </Stack>
          </PopoverHeader>
          <PopoverBody>
            <Stack divider={<Divider />}>
              {Array.from(tasks)
                .reverse()
                .map((task) => (
                  <BackgroundTaskItem
                    key={task.id}
                    task={task}
                    onRemove={handleRemoveTask}
                  />
                ))}
            </Stack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
}

function BackgroundTaskItem({
  task,
  onRemove,
}: {
  task: BackgroundTask;
  onRemove: (id: string) => void;
}) {
  const isResolved = task.status === "resolved";
  const isRunning = task.status === "running";

  const isRemoveable = task.status === "resolved" || task.status === "rejected";
  return (
    <Stack py={2} position={"relative"}>
      <HStack align="start">
        <Box>
          {isRunning ? (
            <Spinner size="xs" />
          ) : (
            <Icon
              as={!isResolved ? TbX : TbCheck}
              bg={!isResolved ? "red.500" : "green"}
              borderRadius="full"
              fontSize="sm"
              p={0.5}
              color="white"
            />
          )}
        </Box>
        <Stack as={RouterLink} to={task.link} spacing={0}>
          <Link as={Text}>{task.name}</Link>
          <Text variant={"subdued"} fontSize="sm">
            {task.type}
          </Text>
        </Stack>
        <Spacer />
        {isRemoveable && (
          <IconButton
            icon={<TbX />}
            variant="ghost"
            aria-label="Remove task"
            onClick={() => onRemove(task.id)}
          />
        )}
      </HStack>
    </Stack>
  );
}
